// Per-folder member management — mirrors invite-to-project-dialog.tsx but
// folder-scoped. Migration 0012 (2026-07-14). Folder ACL is INDEPENDENT of
// project ACL: adding someone to a restricted folder inside a restricted
// project does NOT give them project access (they still need the project row).
import { useMemo, useState } from "react";
import { Check, Loader2, UserPlus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useMembers } from "@/hooks/use-members";
import {
  useAddFolderMember,
  useFolderMembers,
  useRemoveFolderMember,
} from "@/hooks/use-folders";
import { canManageFolderAccess, roleDisplayName } from "@/lib/api-types";
import { authErrorMessage } from "@/lib/auth-errors";
import { cn } from "@/lib/utils";

type Props = {
  folderId: string;
  folderName: string;
  /** Whether this folder is restricted. Shown as a small nudge in the footer. */
  restricted: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Backed by three folder ACL endpoints (worker/routes/editor.js):
 *   GET    /api/editor/folders/:id/members
 *   POST   /api/editor/folders/:id/members       { userId }
 *   DELETE /api/editor/folders/:id/members/:uid
 *
 * All three are ADMIN + FULL_ACCESS only server-side (MANAGE_FOLDER_ACCESS).
 * Only workspace members can be added — this dialog does NOT invite brand-new
 * emails (that flow is Manage Members on the projects page).
 */
export function InviteToFolderDialog({
  folderId,
  folderName,
  restricted,
  open,
  onOpenChange,
}: Props) {
  const { user, role } = useAuth();
  const canManage = canManageFolderAccess(role);

  const membersQuery = useMembers(open && canManage);
  const folderMembersQuery = useFolderMembers(folderId, open && canManage);

  const invite = useAddFolderMember(folderId);
  const remove = useRemoveFolderMember(folderId);

  const [query, setQuery] = useState("");

  const invitedIds = useMemo(
    () => new Set((folderMembersQuery.data ?? []).map((m) => m.userId)),
    [folderMembersQuery.data],
  );

  const suggestions = useMemo(() => {
    const rows = membersQuery.data ?? [];
    const trimmed = query.trim().toLowerCase();
    return rows.filter((m) => {
      if (m.id === user?.id) return false;
      if (invitedIds.has(m.id)) return false;
      if (m.role === "ADMIN") return false;
      if (!trimmed) return true;
      return (
        m.email.toLowerCase().includes(trimmed) ||
        (m.name || "").toLowerCase().includes(trimmed)
      );
    });
  }, [membersQuery.data, query, invitedIds, user?.id]);

  const inviteError = invite.isError ? authErrorMessage(invite.error) : null;
  const removeError = remove.isError ? authErrorMessage(remove.error) : null;

  function handleAdd(userId: string) {
    invite.mutate(userId, { onSuccess: () => setQuery("") });
  }

  function handleClose(next: boolean) {
    if (!next) setQuery("");
    onOpenChange(next);
  }

  if (!canManage) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite to "{folderName}"</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Only Workspace Admin and Full Access users can manage folder
            members. Ask one of them to add you.
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite to "{folderName}"</DialogTitle>
          <DialogDescription>
            Add existing workspace members. To invite a brand-new person, use
            Manage Members on the projects page first.
          </DialogDescription>
        </DialogHeader>

        {/* Picker */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-folder-search">Search workspace members</Label>
          <Input
            id="invite-folder-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name or email"
            autoComplete="off"
          />
        </div>

        <div className="max-h-56 overflow-y-auto rounded-lg border border-border">
          {membersQuery.isLoading ? (
            <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading members…
            </div>
          ) : suggestions.length === 0 ? (
            <div className="py-4 text-center text-xs text-muted-foreground">
              {query
                ? "No matching workspace members."
                : "Everyone in the workspace is already on this folder."}
            </div>
          ) : (
            suggestions.map((m) => {
              const isAdding = invite.isPending && invite.variables === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleAdd(m.id)}
                  disabled={invite.isPending}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left last:border-b-0",
                    "hover:bg-secondary/60 disabled:opacity-60",
                  )}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {m.name || m.email}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {m.email} · {roleDisplayName(m.role)}
                    </div>
                  </div>
                  {isAdding ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    <UserPlus className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {inviteError && <p className="text-sm text-destructive">{inviteError}</p>}

        {/* Current folder members */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">On this folder</p>
            {folderMembersQuery.isFetching && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}
          </div>
          <div className="rounded-lg border border-border">
            {(folderMembersQuery.data ?? []).length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground">
                No members invited yet. Workspace Admin and Full Access users
                always have access.
              </div>
            ) : (
              (folderMembersQuery.data ?? []).map((m) => {
                const isRemoving = remove.isPending && remove.variables === m.userId;
                return (
                  <div
                    key={m.userId}
                    className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {m.name || m.email}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {m.email} · {roleDisplayName(m.role)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove.mutate(m.userId)}
                      disabled={remove.isPending}
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-destructive disabled:opacity-60"
                      title="Remove from folder"
                      aria-label={`Remove ${m.email} from folder`}
                    >
                      {isRemoving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <X className="h-3.5 w-3.5" strokeWidth={2} />
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          {removeError && <p className="text-sm text-destructive">{removeError}</p>}
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" strokeWidth={2.25} />
          {restricted
            ? "This folder is restricted — only invited members and Workspace Admin / Full Access can enter."
            : "This folder is public — added members don't gain anything until you turn on Restricted in Folder settings."}
        </div>
      </DialogContent>
    </Dialog>
  );
}
